import {
  type AccountContactUser,
  type AccountData,
  type ContactData,
  type ExtendedLocationData,
  type LocationActivity,
  type TagId,
  type TagWithDate,
  type TinyLocationData,
  type TinyUser,
  type UserId,
  type WorkflowActionId,
} from '../model'
import { sortByStatusChangedDate } from './accountStatusHistory'
import { getCreatedAt } from './audit'
import { isDefined } from './isEmpty'
import { Logger } from './logger'

const logger = new Logger('activity')

export function toExtendedLocationData(location: LocationActivity): ExtendedLocationData {
  const { latitude, longitude, residents, ...property } = location.property
  return {
    ...property,
    locationId: location.locationId,
    latlng: { latitude, longitude },
    contacts: location.contacts,
    account: location.accounts?.sort(sortAccounts).at(0),
    accounts: location.accounts,
    events: location.events,
    evaluations: location.evaluations,
    notes: location.notes,
    disposition: location.contacts?.sort(sortContacts).at(0)?.disposition,
    residents: residents?.map((residentData) => {
      return {
        ...residentData,
        name: residentData.name ?? `${residentData.firstName} ${residentData.lastName}`,
      }
    }),
  }
}

export function sortContacts(a: ContactData, b: ContactData): number {
  return b.contactDay.localeCompare(a.contactDay)
}

export function sortAccounts(a: AccountData, b: AccountData): number {
  const aSH = a.statusHistory.sort(sortByStatusChangedDate).at(-1)
  const bSH = b.statusHistory.sort(sortByStatusChangedDate).at(-1)
  return (bSH?.statusChangedDate ?? b.updatedAt ?? Date.now()) - (aSH?.statusChangedDate ?? a.updatedAt ?? Date.now())
}

export function locationActivityToTinyLocationData(loc: LocationActivity): TinyLocationData {
  return {
    ...loc,
    address: loc.property.line1,
    city: loc.property.locality,
    state: loc.property.countrySubd,
    zip: loc.property.postal1,
    latitude: loc.property.latitude,
    longitude: loc.property.longitude,
    resident: getLocationResident(loc),
    notes: loc.notes,
    accountContact: getLocationAccountContact(loc),
  }
}

function getLocationResident(loc: LocationActivity): TinyLocationData['resident'] {
  if (loc.property.resident) {
    return loc.property.resident
  }
  return loc.property.residents?.at(0)
}

function getLocationAccountContact(loc: LocationActivity): TinyLocationData['accountContact'] {
  const sortedContacts = loc.contacts?.sort(sortContacts)
  const sortedAccounts = loc.accounts?.sort(sortAccounts)
  const mostRecentContact = sortedContacts?.at(0)
  const mostRecentAccount = sortedAccounts?.at(0)
  return {
    locationId: loc.locationId,
    locationCoordinates: loc.property,
    disposition: sortedContacts?.at(0)?.disposition,
    statusId: sortedAccounts?.at(0)?.statusId,
    user: getLocationContactUser(mostRecentContact, mostRecentAccount),
  }
}

function getLocationContactUser(
  mostRecentContact: ContactData | undefined,
  mostRecentAccount: AccountData | undefined
): AccountContactUser {
  if (mostRecentAccount) {
    const accountContactDate = Math.max(
      ...[mostRecentAccount.updatedAt, getCreatedAt(mostRecentAccount), mostRecentAccount.appointmentDate].filter(
        isDefined
      )
    )
    if (!mostRecentContact || accountContactDate > mostRecentContact.contactDate) {
      return getAccountUser(mostRecentAccount)
    }
  }
  if (mostRecentContact) {
    if (isDefined(mostRecentContact.setter)) return mostRecentContact.setter
    return mostRecentContact.setterId
  }
}

export function getAccountUser(account: AccountData): TinyUser | UserId | undefined {
  if (account.closerId && account.closer) {
    return {
      userId: account.closerId,
      ...account.closer,
    }
  }

  if (account.ownerId && account.owner) {
    return {
      userId: account.ownerId,
      ...account.owner,
    }
  }

  if (account.closerId) return account.closerId
  return account.ownerId
}

export function chooseLatestActivity(
  tag?: TagWithDate,
  action?: { actionId?: WorkflowActionId; timestamp?: number }
): WorkflowActionId | TagId | undefined {
  if (!tag?.timestamp) return action?.actionId
  if (!action?.timestamp) return tag.tagId
  return tag.timestamp > action.timestamp ? tag.tagId : action.actionId
}
